"use client";

// connect/pluginGate.ts — is the plugin way open to this person? (F.nettiConnect)
//
// ChatGPT and Codex connect through the published innernet plugin only behind connect with netti
// (John, 2026-10-30: "ship it as part of the netti connection feature flag. we'll turn it on for
// everybody when it launches with ask netti"). The walk only runs behind that flag, so it applies the
// plugin way itself (lib/connect/tools withPlugin). The sheets and lists are everyone's, and they are
// drawn on a phone too, where the rail (and its data-netti-connect mark) is not: they read the flag
// here. DashboardClient sets it from the same canSee(F.nettiConnect, viewer) that hands NettiDock its
// connect prop, so the two never disagree. Until it is set (and anywhere outside the dashboard) it is
// off: everyone keeps today's steps.

import * as React from "react";
import { withPlugin, type ConnectTool } from "./tools";

let on = false;
const listeners = new Set<() => void>();

/** whether this person is behind connect with netti. `quiet` sets it without telling anyone: the
 *  dashboard primes it while it renders (nothing may be redrawn mid-render), then says it aloud. */
export function setPluginConnect(next: boolean, opts: { quiet?: boolean } = {}): void {
  if (next === on) return;
  on = next;
  if (opts.quiet) return;
  for (const l of listeners) l();
}

/** the dashboard's half: primed on render so a sheet opened on this paint reads it, said after */
export function usePluginConnectFrom(open: boolean): void {
  // never on the server: one module there serves every request
  if (typeof window !== "undefined") setPluginConnect(open, { quiet: true });
  React.useLayoutEffect(() => {
    // whoever subscribed before the prime hears it now
    for (const l of listeners) l();
  }, [open]);
}

/** the flag, read outside React */
export function pluginConnect(): boolean {
  return on;
}

function subscribe(l: () => void): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** the flag, as a hook that redraws when it is set */
export function usePluginConnect(): boolean {
  return React.useSyncExternalStore(subscribe, pluginConnect, () => false);
}

/** the tool as this person should see it: the plugin way when the flag is open, today's otherwise */
export function connectToolView(t: ConnectTool, plugin: boolean): ConnectTool {
  return plugin ? withPlugin(t) : t;
}

/** connectToolView with the flag read here */
export function useConnectToolView(t: ConnectTool): ConnectTool {
  const plugin = usePluginConnect();
  return React.useMemo(() => connectToolView(t, plugin), [t, plugin]);
}
